import { Notice } from '@genial/components';
import { Constant } from '@genial/config';
import { Utils } from '@genial/utils';
import {
  createDrawerNavigator,
  DrawerNavigationOptions,
  DrawerScreenProps,
} from '@react-navigation/drawer';
import {
  LinkingOptions,
  getPathFromState as defaultGetPathFromState,
} from '@react-navigation/native';
import { createNativeStackNavigator } from '@react-navigation/native-stack';
import { IMutableContext, useUnleashContext } from '@unleash/proxy-client-react';
import * as Linking from 'expo-linking';
import { Box } from 'native-base';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Platform } from 'react-native';
import { useRecoilState, useRecoilValue } from 'recoil';

import { AppMenu } from '../components/AppMenu';
import { SignedInHeader } from '../components/SignedInHeader';
import {
  AbsoluteRouteDetector,
  handleScreenFocusChange,
  handleScreenStateChange,
} from '../contexts/AbsoluteRouteDetector';
import { AdemiconConsortium } from '../pages/AdemiconConsortium';
import { AngularFrame } from '../pages/AngularFrame/index.web';
import { AvailableProducts } from '../pages/AvailableProducts';
import { AvailableProductsFiltersModal } from '../pages/AvailableProducts/components/DynamicProduct/components/ProductFilters';
import { AvailableProductsOrderByModal } from '../pages/AvailableProducts/components/DynamicProduct/components/ProductOrderBy';
import { FundsComparator } from '../pages/AvailableProducts/components/FundsComparator';
import { BatchTicket } from '../pages/BatchTicket';
import { BatchTicketCart } from '../pages/BatchTicket/components/Cart';
import { BatchTicketCustomerModalFilter } from '../pages/BatchTicket/components/Customer/components/Filter';
import { BatchTicketAssetFiltersModal } from '../pages/BatchTicket/components/DynamicAsset/components/AssetFilters';
import { BatchTicketAssetOrderByModal } from '../pages/BatchTicket/components/DynamicAsset/components/AssetOrderBy';
import { BatchTicketResume } from '../pages/BatchTicket/components/Resume';
import { BatchTicketResumeDetail } from '../pages/BatchTicket/components/Resume/components/Detail';
import { BatchTicketSent } from '../pages/BatchTicket/components/Sent';
import { BatchTicketDetail } from '../pages/BatchTicketDetail';
import { BatchTicketRequests } from '../pages/BatchTicketRequests';
import { BlockList } from '../pages/BlockList';
import { BlockDetails } from '../pages/BlockList/BlockDetails';
import { UnlockCustomer } from '../pages/BlockList/UnlockCustomer';
import { Chat } from '../pages/Chat';
import { Cockpit } from '../pages/Cockpit';
import { Consortium } from '../pages/Consortium';
import { Sent as ConsortiumSent } from '../pages/Consortium/components/Sent';
import { Details as MyConsortiumDetails } from '../pages/Consortium/components/Tabs/components/MyConsortiums/components/Details';
import { Plans as ConsortiumPlans } from '../pages/Consortium/components/Tabs/components/Simulate/components/Plans';
import { SimulateValue as ConsortiumSimulateValue } from '../pages/Consortium/components/Tabs/components/Simulate/components/SimulateValue';
import { CustomerAssetDetail } from '../pages/CustomerAssetDetail';
import { CustomerDetail } from '../pages/CustomerDetail';
import { CustomerApplicationModal } from '../pages/CustomerDetail/components/Application/components/CustomerApplicationModal';
import { PrivatePensionApplication } from '../pages/CustomerDetail/components/Application/components/CustomerApplicationModal/products/PrivatePensionApplication';
import { PrivatePensionAccountHolderModal } from '../pages/CustomerDetail/components/Application/components/CustomerApplicationModal/products/PrivatePensionApplication/components/PrivatePensionAccountHolderModal';
import { PrivatePensionBeneficiaryModal } from '../pages/CustomerDetail/components/Application/components/CustomerApplicationModal/products/PrivatePensionApplication/components/PrivatePensionBeneficiaryModal';
import { PrivatePensionOrderSummaryModal } from '../pages/CustomerDetail/components/Application/components/CustomerApplicationModal/products/PrivatePensionApplication/components/PrivatePensionOrderSummary';
import { PrivatePensionPortabilityModal } from '../pages/CustomerDetail/components/Application/components/CustomerApplicationModal/products/PrivatePensionApplication/components/PrivatePensionPortabilityModal';
import { PrivatePensionNewApplicationModal } from '../pages/CustomerDetail/components/PrivatePensionContacted/components/PrivatePensionNewApplicationModal';
import { SecuritiesTransferOrderByModal } from '../pages/CustomerDetail/components/SecuritiesTransfer/components/Header/components/Filters/components/SecuritiesTransferOrderByModal';
import { SecuritiesTransferSearchFilter } from '../pages/CustomerDetail/components/SecuritiesTransfer/components/Header/components/Filters/components/SecuritiesTransferSearchFilter';
import { ProfitabilityReportDownload } from '../pages/CustomerDetail/components/Summary/components/ProfitabilityChart/profitabilityReportDownload';
import { MovementFilter } from '../pages/CustomerDetail/components/Transactions/components/Movement/Filter';
import { ReportsFilter } from '../pages/CustomerDetail/components/Transactions/components/Reports/Filter';
import { StatementsFilter } from '../pages/CustomerDetail/components/Transactions/components/Statements/Filter';
import { NewWithdrawal } from '../pages/CustomerDetail/components/Withdrawal/components/NewWithdrawal';
import { Customers } from '../pages/Customers';
import { DividendHistory } from '../pages/DividendHistory';
import { DividendHistoryFilterModal } from '../pages/DividendHistory/DividendHistoryFilterModal';
import { DocumentsCenter } from '../pages/DocumentsCenter';
import { EquityOffersBookModal } from '../pages/EquityOffersBookModal';
import { FixedFee } from '../pages/FixedFee';
import { FixedFeeFilterModal } from '../pages/FixedFee/FilterModal';
import { FixedFeeNewContractModal } from '../pages/FixedFee/NewContractModal';
import { FixedFeeOrderModal } from '../pages/FixedFee/OrderModal';
import { FixedFeeReportModal } from '../pages/FixedFee/ReportModal';
import { FixedFeeTermHistoryModal } from '../pages/FixedFee/TermHistoryModal';
import { GenialReport } from '../pages/GenialReport';
import { Home } from '../pages/Home';
import { FinancialSummaryFilter } from '../pages/Home/components/FinancialSummary/Filter';
import { InvestmentStrategyFilter as HomeInvestmentStrategyFilterDiversifier } from '../pages/Home/components/InvestmentStrategy/components/Diversifier/InvestmentStrategyFilter';
import { InvestmentStrategyFilter as HomeInvestmentStrategyFilterPositive } from '../pages/Home/components/InvestmentStrategy/components/Positive/InvestmentStrategyFilter';
import { InvestmentStrategyMobile as HomeInvestmentStrategyMobile } from '../pages/Home/components/InvestmentStrategy/Mobile';
import { HomeFinancialSummary } from '../pages/HomeFinancialSummary';
import { HomeTop15Products } from '../pages/HomeTop15Products';
import { ImpersonateMenu, ImpersonateMenuProvider } from '../pages/ImpersonateMenu';
import { InviteLinkModal } from '../pages/InviteLinkModal';
import { ManagementAnalysis } from '../pages/ManagementAnalysis';
import { ManagementAnalysisDetails } from '../pages/ManagementAnalysisDetails';
import { ManagementAnalysisDetailsModalFilter } from '../pages/ManagementAnalysisDetails/components/Filter';
import { NewApplication } from '../pages/NewApplication';
import { NewApplicationFilter } from '../pages/NewApplication/Filter';
import { NotFound } from '../pages/NotFound';
import { OrderHistoryModal } from '../pages/OrderHistoryModal';
import { EquityOrderEdit } from '../pages/OrderHistoryModal/components/EquityOrderEdit';
import { OrdersHistory } from '../pages/OrdersHistory';
import { OrderHistoryFiltersModal } from '../pages/OrdersHistory/Container/components/OrderHistoryFilters';
import { OrderHistoryOrderByModal } from '../pages/OrdersHistory/Container/components/OrderHistoryOrderBy';
import { ProductsManagement } from '../pages/ProductsManagement';
import { ProductManagementFiltersModal } from '../pages/ProductsManagement/components/Filters/Mobile';
import { ProductsManagementBlockModal } from '../pages/ProductsManagement/components/ProductsManagementBlockModal';
import { ProductsManagementDetails } from '../pages/ProductsManagementDetails';
import { RateManagement } from '../pages/RateManagement';
import { RequestDetails } from '../pages/RateManagement/RequestDetails';
import { ConfirmMasterAccount } from '../pages/RateManagement/RequestDetails/components/ConfirmMasterAccount';
import { RateManagementUpload } from '../pages/RateManagement/Upload';
import { RedeemBatchTicket } from '../pages/RedeemBatchTicket';
import { RedeemBatchTicketDetails } from '../pages/RedeemBatchTicketDetails';
import { RedeemBatchTicketDetailsFilter } from '../pages/RedeemBatchTicketDetailsFilter';
import { RedemptionSolicitation } from '../pages/RedemptionSolicitation';
import { ReportGenerator } from '../pages/ReportGenerator';
import { ReportGeneratorDetailsModal } from '../pages/ReportGenerator/components/ReportGeneratorDetailsModal';
import { ReportGeneratorFilterByModal } from '../pages/ReportGenerator/components/ReportGeneratorFilterByModal';
import { ReportGeneratorOrderByModal } from '../pages/ReportGenerator/components/ReportGeneratorOrderByModal';
import { ReportGeneratorRequestModal } from '../pages/ReportGenerator/components/ReportGeneratorRequestModal';
import { Resume } from '../pages/Resume';
import { SecuritiesTransferDetail } from '../pages/SecuritiesTransferDetail';
import { SecuritiesTransferHistoryModal } from '../pages/SecuritiesTransferHistoryModal';
import { SignOut } from '../pages/SignOut';
import { Subscription } from '../pages/Subscription';
import { SubscriptionFiltersModal } from '../pages/Subscription/components/SubscriptionFilters';
import { SubscriptionDetails } from '../pages/Subscription/SubscriptionDetails';
import { SubscriptionSolicitation } from '../pages/Subscription/SubscriptionSolicitation';
import { Trading } from '../pages/Trading';
import { TradingDetails } from '../pages/Trading/TradingDetails';
import { TransactionFlow } from '../pages/TransactionFlow';
import { HistoricalSearchFiltersModal } from '../pages/TransactionFlow/components/HistoricalSearch/Filters/HistoricalSearchFiltersModal';
import { TransactionFlowDetails } from '../pages/TransactionFlowDetails';
import { fetchPermissions, userPermissions } from '../store/permission/atom';
import { UserPermissions } from '../store/permission/types';
import { useSessionActions } from '../store/session/actions';
import { sessionState } from '../store/session/atom';
import { FeatureToggle, useUnleashFlags } from '../utils/useUnleashFlags';

import { angularFrameRoutesParams } from './constants';
import { useInitialRouteName } from './hooks';
import {
  clearLinkingError,
  LINKING_INVALID_PARAMS_MESSAGE,
  sanitizeNavigationState,
  subscribeLinkingErrors,
} from './linkingHelpers';
import { signedInLinkingScreens } from './routeDefinitions';
import type { CustomerDetailStackParamList } from './types';
import { AngularFrameRoutes, SignedInRootStackParamList } from './types';

export const linking: LinkingOptions<any> = {
  prefixes: [Linking.createURL('/')],
  getPathFromState: (state, options) =>
    defaultGetPathFromState(sanitizeNavigationState(state), options),
  config: {
    initialRouteName: 'AppDrawer',
    screens: {
      AppDrawer: {
        path: '/',
        screens: signedInLinkingScreens,
      },
    },
  },
};

const {
  Navigator: DrawerNavigator,
  Screen: DrawerScreen,
  Group: DrawerGroup,
} = createDrawerNavigator<SignedInRootStackParamList & AngularFrameRoutes>();

const {
  Navigator: StackNavigator,
  Group: StackGroup,
  Screen: StackScreen,
} = createNativeStackNavigator<SignedInRootStackParamList & { AppDrawer: undefined }>();

const { Navigator: CustomerDetailNavigator, Screen: CustomerDetailScreen } =
  createNativeStackNavigator<CustomerDetailStackParamList>();

const hiddenDrawerRoute: DrawerNavigationOptions = { drawerItemStyle: { display: 'none' } };

const NullComponent = () => null;

/**
 * CustomerDetailGroup is a nested navigator that contains CustomerDetail and PrivatePensionApplication screens.
 * We need to manually forward route.params from the parent (Drawer) to child screens because
 * React Navigation doesn't automatically propagate params from parent navigators to nested child screens.
 * Without this, path params (customerId, accountId) parsed at the group level would not be available
 * in the CustomerDetail screen when navigating via deep links.
 */
const CustomerDetailGroup: React.FC<
  DrawerScreenProps<SignedInRootStackParamList, 'CustomerDetailGroup'>
> = ({ route }) => {
  // Extract only the shared params that both screens need
  const sharedParams = route.params
    ? {
        customerId: route.params.customerId,
        accountId: route.params.accountId,
        accountNumber: route.params.accountNumber,
        cpfCnpj: route.params.cpfCnpj,
        cpfCnpjFormatted: route.params.cpfCnpjFormatted,
      }
    : undefined;

  return (
    <CustomerDetailNavigator
      initialRouteName="CustomerDetail"
      screenOptions={{ headerShown: false }}>
      <CustomerDetailScreen
        name="CustomerDetail"
        component={CustomerDetail}
        initialParams={route.params}
        options={{ title: 'Carregando - Detalhes do cliente' }}
      />
      <CustomerDetailScreen
        name="PrivatePensionApplication"
        component={PrivatePensionApplication}
        initialParams={sharedParams}
      />
      <CustomerDetailScreen
        name="SecuritiesTransferDetail"
        component={SecuritiesTransferDetail}
        initialParams={sharedParams}
      />
    </CustomerDetailNavigator>
  );
};

const AppDrawer = () => {
  const [permissions, setPermissions] = useRecoilState<UserPermissions>(userPermissions);

  const { setCurrentRoutes, setCurrentRoute } = useContext(AbsoluteRouteDetector.Context);
  const { hasRefreshTokenObserver, createRefreshTokenObserver } = useSessionActions();
  const updateContext = useUnleashContext();
  const { userProfile } = useRecoilValue(sessionState);
  const { name: initialRouteName } = useInitialRouteName();
  const {
    b2bShowModuleHome,
    b2bShowModuleLegacyHome,
    b2bShowModuleCustomers,
    b2bShowModuleFastTicket,
    b2bShowModuleManagementAnalysis,
    b2bShowModuleRateManagement,
    b2bShowModuleTrading,
    b2bShowModuleAdemiconConsortium,
    b2bShowModuleConsortium,
    b2bShowGenialReport,
    b2bShowInvitationAdvisor,
    b2bShowModuleReportGenerator,
    b2bEnableFixedFee,
  } = useUnleashFlags(
    FeatureToggle.Home,
    FeatureToggle.LegacyHome,
    FeatureToggle.Customers,
    FeatureToggle.FastTicket,
    FeatureToggle.ManagementAnalysis,
    FeatureToggle.RateManagement,
    FeatureToggle.Trading,
    FeatureToggle.AdemiconConsortium,
    FeatureToggle.Consortium,
    FeatureToggle.GenialReport,
    FeatureToggle.InviteLink,
    FeatureToggle.ReportGenerator,
    FeatureToggle.FixedFee
  );

  useEffect(() => {
    if (userProfile?.idAssessorIntranet) {
      window.dtrum?.identifyUser(`${userProfile.nomeRecurso} (${userProfile.idAssessorIntranet})`);
    }
  }, [userProfile?.idAssessorIntranet]);

  useEffect(() => {
    const unleashContext = {
      assessor: userProfile?.idAssessorIntranet,
      email: userProfile?.email,
      idfilial: userProfile?.idFilialParceiroIntranet,
      idtipoparceiro: userProfile?.idTipoParceiro,
      idparceiro: userProfile?.idParceiro,
      idgrupoassessor: userProfile?.grupoAssessor,
    } as IMutableContext;

    updateContext(unleashContext);
  }, [userProfile, updateContext]);

  useEffect(() => {
    if (hasRefreshTokenObserver) {
      return;
    }

    createRefreshTokenObserver();
  }, [createRefreshTokenObserver, hasRefreshTokenObserver]);

  useEffect(() => {
    const updatePermissions = async () => {
      const jwtToken = await Utils.Storage.GetItem(Constant.TokenKey);
      const userProfileData = await Utils.Storage.GetItem(Constant.UserProfileKey);

      if (jwtToken && userProfileData) {
        const newPermissions = await fetchPermissions();
        setPermissions(newPermissions);
      } else {
        setPermissions({ permissionAccess: {} });
      }
    };

    updatePermissions();
  }, [setPermissions]);

  return (
    <DrawerNavigator
      initialRouteName={initialRouteName}
      drawerContent={AppMenu}
      screenListeners={{
        focus: handleScreenFocusChange(setCurrentRoute),
        state: handleScreenStateChange(setCurrentRoutes),
      }}
      screenOptions={{
        title: 'Parceiros - Genial Investimentos',
        unmountOnBlur: true,
        header: AppMenu.CombinedHeader,
        gestureHandlerProps: {
          ...Platform.select({ web: { userSelect: 'text' } }),
        },
        sceneContainerStyle: {
          flexDirection: 'row-reverse',
        },
      }}>
      {b2bShowModuleHome && (
        <DrawerGroup key="drawerHomeGroup">
          <DrawerScreen options={{ title: 'Home' }} name="Home" component={Home} />
          <DrawerScreen
            options={{ title: 'Estratégia de investimento', ...hiddenDrawerRoute }}
            name="HomeInvestmentStrategyMobile"
            initialParams={{ isScreen: true }}
            component={HomeInvestmentStrategyMobile}
          />
        </DrawerGroup>
      )}
      {b2bShowModuleLegacyHome && (
        <DrawerScreen
          options={{ title: 'Home' }}
          initialParams={{ state: 'new-app.home' }}
          name="AngularFrame_Home"
          component={AngularFrame}
        />
      )}
      {b2bShowModuleLegacyHome && (
        <DrawerScreen
          options={{ title: 'Dashboard' }}
          initialParams={angularFrameRoutesParams.AngularFrame_Dashboard}
          name="AngularFrame_Dashboard"
          component={AngularFrame}
        />
      )}
      {/*
      <DrawerScreen
        options={{ title: 'Vinculados' }}
        initialParams={{ state: 'new-app.related-persons' }}
        name="AngularFrame_Linked"
        component={AngularFrame}
      />
      */}
      {permissions?.permissionAccess?.consolidated && (
        <DrawerScreen
          options={{ title: 'Carteira Consolidada' }}
          initialParams={angularFrameRoutesParams.AngularFrame_ConsolidatedPortfolio}
          name="AngularFrame_ConsolidatedPortfolio"
          component={AngularFrame}
        />
      )}
      {b2bShowModuleFastTicket && (
        <DrawerScreen options={{ title: 'Cockpit' }} name="Cockpit" component={Cockpit} />
      )}
      {b2bShowModuleManagementAnalysis && (
        <>
          <DrawerScreen
            options={{ title: 'Análise Gerencial' }}
            name="ManagementAnalysis"
            component={ManagementAnalysis}
          />

          <DrawerScreen
            options={{ title: 'Análise Gerencial - Detalhes', ...hiddenDrawerRoute }}
            name="ManagementAnalysisDetails"
            component={ManagementAnalysisDetails}
          />
        </>
      )}
      <DrawerScreen
        options={{ title: 'Análise Gerencial' }}
        initialParams={angularFrameRoutesParams.AngularFrame_ManagementAnalysis}
        name="AngularFrame_ManagementAnalysis"
        component={AngularFrame}
      />
      {b2bShowModuleRateManagement && (
        <DrawerScreen
          options={{ title: 'Gerenciamento de Taxas' }}
          name="RateManagement"
          component={RateManagement}
        />
      )}
      {b2bShowModuleCustomers && (
        <DrawerScreen options={{ title: 'Meus Clientes' }} name="Customers" component={Customers} />
      )}
      <DrawerScreen
        name="CustomerDetailGroup"
        component={CustomerDetailGroup}
        options={{ ...hiddenDrawerRoute }}
      />
      <DrawerScreen
        name="CustomersAssetsDetails"
        component={CustomerAssetDetail}
        options={{ ...hiddenDrawerRoute }}
      />
      <DrawerScreen
        options={{ title: 'Meus Clientes' }}
        initialParams={angularFrameRoutesParams.AngularFrame_Customers}
        name="AngularFrame_Customers"
        component={AngularFrame}
      />
      {b2bShowModuleReportGenerator && userProfile?.masterParceiro && (
        <DrawerScreen
          options={{ title: 'Gerador de relatórios' }}
          name="ReportGenerator"
          component={ReportGenerator}
        />
      )}
      {b2bShowModuleAdemiconConsortium && (
        <DrawerScreen
          options={{ title: 'Consórcio' }}
          name="AdemiconConsortium"
          component={AdemiconConsortium}
        />
      )}
      <DrawerScreen
        options={{ title: 'Detalhes do Cliente', ...hiddenDrawerRoute }}
        name="AngularFrame_CustomerDetail"
        component={AngularFrame}
      />
      {permissions?.permissionAccess?.compliance && permissions?.permissionAccess?.screen && (
        <DrawerScreen
          options={{ title: 'Gerador de Ordens' }}
          initialParams={angularFrameRoutesParams.AngularFrame_OrderGenerator}
          name="AngularFrame_OrderGenerator"
          component={AngularFrame}
        />
      )}
      {userProfile?.exibirComissoes && (
        <DrawerScreen
          options={{ title: 'Comissões' }}
          initialParams={angularFrameRoutesParams.AngularFrame_Commissions}
          name="AngularFrame_Commissions"
          component={AngularFrame}
        />
      )}
      {userProfile?.fixedFee && b2bEnableFixedFee && (
        <DrawerScreen options={{ title: 'Fee Fixo' }} name="FixedFee" component={FixedFee} />
      )}
      <DrawerGroup key="CockpitGroup">
        <DrawerScreen
          options={hiddenDrawerRoute}
          name="NewApplication"
          component={NewApplication}
        />
        <DrawerScreen options={hiddenDrawerRoute} name="OrdersHistory" component={OrdersHistory} />
        <DrawerScreen
          options={hiddenDrawerRoute}
          name="DividendHistory"
          component={DividendHistory}
        />
        <DrawerScreen
          options={{ ...hiddenDrawerRoute, title: 'Edição de Ordem' }}
          name="EquityOrderEdit"
          component={EquityOrderEdit}
        />
        <DrawerScreen
          options={{ title: 'Clientes Restritos', ...hiddenDrawerRoute }}
          name="BlockList"
          component={BlockList}
        />
        <DrawerScreen options={hiddenDrawerRoute} name="Resume" component={Resume} />
        <DrawerScreen
          options={{ title: 'Eventos de Subscrição', ...hiddenDrawerRoute }}
          name="Subscription"
          component={Subscription}
        />
        <DrawerScreen
          options={{ title: 'Detalhes de Subscrição', ...hiddenDrawerRoute }}
          name="SubscriptionDetails"
          component={SubscriptionDetails}
        />
        <DrawerScreen options={hiddenDrawerRoute} name="BatchTicket" component={BatchTicket} />
        <DrawerScreen
          options={hiddenDrawerRoute}
          name="BatchTicketRequests"
          component={BatchTicketRequests}
        />
        <DrawerScreen
          options={{ title: 'Detalhes Resgate Boleta em lote', ...hiddenDrawerRoute }}
          name="RedeemBatchTicketDetails"
          component={RedeemBatchTicketDetails}
        />
        <DrawerScreen
          options={hiddenDrawerRoute}
          name="BatchTicketResume"
          component={BatchTicketResume}
        />
        <DrawerScreen
          options={hiddenDrawerRoute}
          name="BatchTicketSent"
          component={BatchTicketSent}
        />
        <DrawerScreen
          options={{ title: 'Comparador de Fundos', ...hiddenDrawerRoute }}
          name="FundsComparator"
          component={FundsComparator}
        />
        <DrawerScreen
          options={{ title: 'Produtos Disponíveis', ...hiddenDrawerRoute }}
          name="AvailableProducts"
          component={AvailableProducts}
        />
      </DrawerGroup>
      <DrawerScreen
        options={{ title: 'Fluxo de Movimentações', ...hiddenDrawerRoute }}
        initialParams={angularFrameRoutesParams.AngularFrame_TransactionFlow}
        name="AngularFrame_TransactionFlow"
        component={AngularFrame}
      />
      {userProfile?.isCarrinho && (
        <DrawerScreen
          options={{ title: 'Carrinho de Compras', ...hiddenDrawerRoute }}
          initialParams={angularFrameRoutesParams.AngularFrame_ShoppingCart}
          name="AngularFrame_ShoppingCart"
          component={AngularFrame}
        />
      )}
      <DrawerScreen
        options={{ title: 'Produtos Disponíveis', ...hiddenDrawerRoute }}
        initialParams={angularFrameRoutesParams.AngularFrame_AvailableProducts}
        name="AngularFrame_AvailableProducts"
        component={AngularFrame}
      />
      <DrawerScreen
        options={{ title: 'Controle de Produtos', ...hiddenDrawerRoute }}
        name="ProductsManagement"
        component={ProductsManagement}
      />
      <DrawerScreen
        options={{ title: 'Controle de Produtos detalhes', ...hiddenDrawerRoute }}
        name="ProductsManagementDetails"
        component={ProductsManagementDetails}
      />
      {(userProfile?.masterParceiro || userProfile?.masterGrupo) && (
        <DrawerScreen
          options={{ title: 'Controle de Produtos', ...hiddenDrawerRoute }}
          initialParams={angularFrameRoutesParams.AngularFrame_ProductControl}
          name="AngularFrame_ProductControl"
          component={AngularFrame}
        />
      )}
      {userProfile?.ExibirSeguros && (
        <DrawerScreen
          options={{ title: 'Seguro de Vida', ...hiddenDrawerRoute }}
          initialParams={angularFrameRoutesParams.AngularFrame_LifeInsurance}
          name="AngularFrame_LifeInsurance"
          component={AngularFrame}
        />
      )}
      {(userProfile?.isGestor || userProfile?.masterParceiro) && (
        <DrawerScreen
          options={{ title: 'Níveis de Acesso', ...hiddenDrawerRoute }}
          initialParams={angularFrameRoutesParams.AngularFrame_AccessLevels}
          name="AngularFrame_AccessLevels"
          component={AngularFrame}
        />
      )}
      <DrawerScreen
        options={{ title: 'Histórico de proventos', ...hiddenDrawerRoute }}
        initialParams={angularFrameRoutesParams.AngularFrame_DividendHistory}
        name="AngularFrame_DividendHistory"
        component={AngularFrame}
      />
      <DrawerScreen
        options={{ title: 'Central de documentos', ...hiddenDrawerRoute }}
        name="DocumentsCenter"
        component={DocumentsCenter}
      />
      <DrawerScreen
        options={{ title: 'Eventos Voluntários', ...hiddenDrawerRoute }}
        initialParams={angularFrameRoutesParams.AngularFrame_VoluntaryEvents}
        name="AngularFrame_VoluntaryEvents"
        component={AngularFrame}
      />
      <DrawerScreen options={hiddenDrawerRoute} name="NotFound" component={NotFound} />
      {b2bShowModuleTrading && (
        <DrawerScreen
          options={{ title: 'Day Trade', ...hiddenDrawerRoute }}
          name="Trading"
          component={Trading}
        />
      )}
      {b2bShowModuleConsortium && (
        <DrawerScreen
          options={{ title: 'Consórcio', ...hiddenDrawerRoute }}
          name="Consortium"
          component={Consortium}
        />
      )}
      {b2bShowModuleConsortium && (
        <DrawerScreen
          options={{ title: 'Consórcio', ...hiddenDrawerRoute }}
          name="ConsortiumSent"
          component={ConsortiumSent}
        />
      )}
      <DrawerScreen
        options={{ title: 'Fluxo de Movimentações', ...hiddenDrawerRoute }}
        name="TransactionFlow"
        component={TransactionFlow}
      />
      <DrawerScreen
        name="TransactionFlowDetails"
        options={{ title: 'Posição detalhada', ...hiddenDrawerRoute }}
        component={TransactionFlowDetails}
      />
      {/*
      {!b2bShowModuleTrading && permissionAccess.trading && (
        <DrawerScreen
          options={{ title: 'Trading' }}
          initialParams={{ ...angularFrameRoutesParams.AngularFrame_Trading, ...hiddenDrawerRoute }}
          name="AngularFrame_Trading"
          component={AngularFrame}
        />
      )}
      */}
      {b2bShowInvitationAdvisor && (
        <DrawerScreen
          options={{ title: 'Link para convite' }}
          name="InviteLinkPlaceholder"
          component={NullComponent}
        />
      )}
      <DrawerScreen options={hiddenDrawerRoute} name="TradingDetails" component={TradingDetails} />
      <DrawerScreen
        options={{ title: 'Meu Perfil', ...hiddenDrawerRoute }}
        initialParams={angularFrameRoutesParams.AngularFrame_MyProfile}
        name="AngularFrame_MyProfile"
        component={AngularFrame}
      />
      <DrawerScreen options={hiddenDrawerRoute} name="SignOut" component={SignOut} />
      {b2bShowGenialReport && (
        <DrawerScreen
          options={{ title: 'Genial Report' }}
          name="GenialReport"
          component={GenialReport}
        />
      )}
    </DrawerNavigator>
  );
};

export const SignedIn = () => {
  const { setCurrentRoutes, setCurrentRoute } = useContext(AbsoluteRouteDetector.Context);
  const { b2bShowModuleChat } = useUnleashFlags(FeatureToggle.Chat);
  const [linkingError, setLinkingError] = useState<string | null>(null);

  useEffect(() => {
    const unsubscribe = subscribeLinkingErrors(setLinkingError);

    return unsubscribe;
  }, []);

  const handleDismissLinkingError = useCallback(() => {
    clearLinkingError();
  }, []);

  return (
    <AbsoluteRouteDetector.Provider>
      <ImpersonateMenuProvider>
        <AppMenu.Provider>
          {linkingError && (
            <Box px="4" pt="3">
              <Notice
                my="0"
                testID="linking-error-notice"
                type="alert"
                onPress={handleDismissLinkingError}
                textObjects={[
                  {
                    text: linkingError ?? LINKING_INVALID_PARAMS_MESSAGE,
                    variant: 'bold',
                  },
                ]}
              />
            </Box>
          )}
          <StackNavigator
            initialRouteName="AppDrawer"
            screenListeners={{
              focus: handleScreenFocusChange(setCurrentRoute),
              state: handleScreenStateChange(setCurrentRoutes),
            }}
            screenOptions={{
              /**
               * NOTE: Rendering is done this way due Recoil behavior that cause a subtree bug when opening modals, but it's
               * not causing unwanted re-renders
               */

              header: () => <SignedInHeader />,
              contentStyle: {
                overflow: 'hidden',
              },
            }}>
            <StackScreen name="AppDrawer" component={AppDrawer} />
            <StackGroup
              screenOptions={{
                presentation: 'containedTransparentModal',
                headerShown: false,
              }}>
              <StackScreen name="UnlockCustomer" component={UnlockCustomer} />
              <StackScreen
                name="HomeInvestmentStrategyFilterDiversifier"
                component={HomeInvestmentStrategyFilterDiversifier}
              />
              <StackScreen
                name="HomeInvestmentStrategyFilterPositive"
                component={HomeInvestmentStrategyFilterPositive}
              />
              <StackScreen
                name="SecuritiesTransferSearchFilter"
                component={SecuritiesTransferSearchFilter}
              />
              <StackScreen
                name="SecuritiesTransferOrderByModal"
                component={SecuritiesTransferOrderByModal}
              />
              <StackScreen
                name="SecuritiesTransferHistoryModal"
                component={SecuritiesTransferHistoryModal}
              />
              <StackScreen name="RequestDetails" component={RequestDetails} />
              <StackScreen name="OrderHistoryModal" component={OrderHistoryModal} />
              <StackScreen name="OrderHistoryFiltersModal" component={OrderHistoryFiltersModal} />
              <StackScreen name="OrderHistoryOrderByModal" component={OrderHistoryOrderByModal} />
              <StackScreen
                name="ManagementAnalysisDetailsModalFilter"
                component={ManagementAnalysisDetailsModalFilter}
              />
              <StackScreen name="MovementFilter" component={MovementFilter} />
              <StackScreen name="RateManagementUpload" component={RateManagementUpload} />
              <StackScreen name="NewApplicationFilter" component={NewApplicationFilter} />
              <StackScreen name="FinancialSummaryFilter" component={FinancialSummaryFilter} />
              <StackScreen name="ReportsFilter" component={ReportsFilter} />
              <StackScreen name="StatementsFilter" component={StatementsFilter} />
              <StackScreen
                name="DividendHistoryFilterModal"
                component={DividendHistoryFilterModal}
              />
              <StackScreen name="FixedFeeFilterModal" component={FixedFeeFilterModal} />
              <StackScreen name="FixedFeeOrderModal" component={FixedFeeOrderModal} />
              <StackScreen name="FixedFeeReportModal" component={FixedFeeReportModal} />
              <StackScreen
                name="FixedFeeNewContractModal"
                component={FixedFeeNewContractModal}
                options={{ title: 'Nova contratação' }}
              />
              <StackScreen name="FixedFeeTermHistoryModal" component={FixedFeeTermHistoryModal} />
              <StackScreen
                name="MyConsortiumDetails"
                options={{ title: 'Detalhes do Consórcio' }}
                component={MyConsortiumDetails}
              />
              <StackScreen
                name="ConsortiumSimulateValue"
                options={{ title: 'Simulador de consórcio' }}
                component={ConsortiumSimulateValue}
              />
              <StackScreen
                name="ProductsManagementBlockModal"
                options={{ title: 'Adicionar bloqueio' }}
                component={ProductsManagementBlockModal}
              />
              <StackScreen
                name="ProductManagementFiltersModal"
                component={ProductManagementFiltersModal}
              />
              <StackScreen
                name="ConsortiumPlans"
                options={{ title: 'Planos de Consórcio' }}
                component={ConsortiumPlans}
              />
              <StackScreen
                name="HistoricalSearchFiltersModal"
                component={HistoricalSearchFiltersModal}
              />
              <StackScreen
                name="ProfitabilityReportDownload"
                component={ProfitabilityReportDownload}
              />
              <StackScreen name="BlockDetails" component={BlockDetails} />
              <StackScreen name="HomeFinancialSummary" component={HomeFinancialSummary} />
              <StackScreen name="HomeTop15Products" component={HomeTop15Products} />
              <StackScreen name="BatchTicketDetail" component={BatchTicketDetail} />
              <StackScreen
                name="AvailableProductsFiltersModal"
                component={AvailableProductsFiltersModal}
              />
              <StackScreen
                name="AvailableProductsOrderByModal"
                component={AvailableProductsOrderByModal}
              />
              <StackScreen
                name="BatchTicketAssetFiltersModal"
                component={BatchTicketAssetFiltersModal}
              />
              <StackScreen
                name="BatchTicketAssetOrderByModal"
                component={BatchTicketAssetOrderByModal}
              />
              <StackScreen
                name="ReportGeneratorFilterByModal"
                component={ReportGeneratorFilterByModal}
              />
              <StackScreen
                name="ReportGeneratorRequestModal"
                component={ReportGeneratorRequestModal}
              />
              <StackScreen
                name="ReportGeneratorDetailsModal"
                component={ReportGeneratorDetailsModal}
              />
              <StackScreen
                name="ReportGeneratorOrderByModal"
                component={ReportGeneratorOrderByModal}
              />
              <StackScreen
                name="RedeemBatchTicketDetailsFilter"
                component={RedeemBatchTicketDetailsFilter}
              />
              <StackScreen
                name="BatchTicketCustomerModalFilter"
                component={BatchTicketCustomerModalFilter}
              />
              <StackScreen
                name="RedeemBatchTicket"
                component={RedeemBatchTicket}
                options={{ title: 'Resgate Boleta em lote' }}
              />
              <StackScreen name="BatchTicketCart" component={BatchTicketCart} />
              <StackScreen name="BatchTicketResumeDetail" component={BatchTicketResumeDetail} />
              <StackScreen
                name="EquityOffersBookModal"
                component={EquityOffersBookModal}
                options={{ title: 'Livro de ofertas' }}
              />
              <StackScreen
                name="RedemptionSolicitation"
                component={RedemptionSolicitation}
                options={{ title: 'Solicitação de Resgate' }}
              />
              <StackScreen
                name="SubscriptionSolicitation"
                component={SubscriptionSolicitation}
                options={{ title: 'Solicitação de Subscrição' }}
              />
              <StackScreen name="SubscriptionFiltersModal" component={SubscriptionFiltersModal} />
              <StackScreen
                name="CustomerApplicationModal"
                component={CustomerApplicationModal}
                options={{ title: 'Aplicação em Meus Clientes' }}
              />
              <StackScreen
                name="PrivatePensionNewApplicationModal"
                component={PrivatePensionNewApplicationModal}
                options={{ title: 'Aplicação em Previdência' }}
              />
              <StackScreen
                name="ImpersonateMenu"
                component={ImpersonateMenu}
                options={{ title: 'Perfil do parceiro' }}
              />
              <StackScreen
                name="NewWithdrawal"
                component={NewWithdrawal}
                options={{ title: 'Novo Saque' }}
              />
              <StackScreen name="InviteLink" component={InviteLinkModal} />
              <StackScreen name="ConfirmMasterAccount" component={ConfirmMasterAccount} />
              <StackScreen
                name="CustomerDetailPrivatePensionBeneficiary"
                component={PrivatePensionBeneficiaryModal}
              />
              <StackScreen
                name="CustomerDetailPrivatePensionPortability"
                component={PrivatePensionPortabilityModal}
              />
              <StackScreen
                name="CustomerDetailPrivatePensionAccountHolder"
                component={PrivatePensionAccountHolderModal}
              />
              <StackScreen
                name="CustomerDetailPrivatePensionOrderSummaryModal"
                component={PrivatePensionOrderSummaryModal}
              />
            </StackGroup>
          </StackNavigator>
          {b2bShowModuleChat && <Chat />}
        </AppMenu.Provider>
      </ImpersonateMenuProvider>
    </AbsoluteRouteDetector.Provider>
  );
};
